<script setup>
import { computed } from 'vue';

import literacyIc01 from '@/assets/images/result/literacy_ic_01.png'; 
import literacyIc02 from '@/assets/images/result/literacy_ic_02.png'; 
import literacyIc03 from '@/assets/images/result/literacy_ic_03.png'; 
import literacyIc04 from '@/assets/images/result/literacy_ic_04.png'; 

import numeracyIc01 from '@/assets/images/result/numeracy_ic_01.png'; 
import numeracyIc02 from '@/assets/images/result/numeracy_ic_02.png'; 
import numeracyIc03 from '@/assets/images/result/numeracy_ic_03.png'; 
import numeracyIc04 from '@/assets/images/result/numeracy_ic_04.png'; 


const props = defineProps({
  type: { 
    type: String,
    required: true,
    validator: (value) => ['literacy', 'numeracy'].includes(value),
  },
  studentName: { 
    type: String,
    default: '',
  },
  subAreaLevels: {
    type: Array,
    default: () => []
  },
  schoolType: {
    type: String,
    default: 'E'
  },
  grade: {
    type: Number,
    default: 4
  }
});

const attainmentLevels = ['주의', '경계', '안전']; 
const allContent = {
  literacy: {
    cardTitle: '하위 영역별 기초 문해력 도달도*와 활용',
    explanationText: `문해력의 각 하위 영역에서 기초 수준에 도달했는지를 나타냅니다. <strong class="font-medium">1수준과 2수준을 구분하는 컷 점수</strong> 지점의 능력을 가진 학생이 해당 영역의 각 점수를 획득할 확률을 구하여, 아래로부터 누적확률이 16%에 대응하는 원점수보다 낮으면 ‘주의’, 84%에 대응하는 원점수보다 높으면 ‘안전’, 그사이의 원점수에 해당하면 ‘경계’입니다. 해석에 있어 ‘주의’는 해당 하위 영역에서 기초 수준에 도달하지 못한 단계, ‘경계’는 기초 수준의 경계에 있는 단계, ‘안전’은 기초 수준 이상에 있는 단계를 의미합니다. 체크된 부분은 학생의 해당 하위 영역별 도달 수준을 나타냅니다.`,
    icons: {
      '어휘': literacyIc01,
      '탐색 및 확인': literacyIc03,
      '통합 및 해석': literacyIc02,
      '비판 및 평가': literacyIc04,
    },
    adviceData: {
      'E_4': {
        '어휘': {
          '주의': '일상생활에서 사용되는 친숙한 단어의 뜻을 정확하게 이해하려는 노력이 필요합니다. 국어사전 등을 활용하여 단어의 의미를 정확하게 파악하고 반복적으로 익히는 습관을 지니면 어휘력 향상에 도움이 됩니다.',
          '경계': '일상생활에서 친숙한 단어의 뜻을 이해하고 비슷한 말과 반대되는 말을 찾아보려는 노력이 필요합니다. 예문을 통해 단어의 다양한 쓰임을 이해하면 상황에 어울리게 단어를 활용하는 데 도움이 됩니다.',
          '안전': '친숙한 단어뿐만 아니라 학습에 필요한 단어의 뜻을 생각해 보고 문맥에서 의미를 짐작해 봅니다. 다양한 종류의 글을 읽으면 단어를 상황에 어울리게 활용하는 능력을 키우는 데 도움이 됩니다.'
        },
        '탐색 및 확인': {
          '주의': '그림이나 도표가 함께 제시된 짧은 글을 읽고 밑줄을 그으며 필요한 정보를 찾아봅니다. 글의 내용을 정확하게 파악하려면 문장이나 단락을 소리 내어 읽는 연습을 꾸준히 하기를 권장합니다.',
          '경계': '친숙한 소재의 글을 읽고 주요 내용을 파악해 봅니다. 각 문단에서 자주 등장하거나 글쓴이가 말하고 싶어 하는 내용을 찾아 밑줄을 그으며 꼼꼼하게 읽으면 글의 중심 생각을 파악하는 데 도움이 됩니다.',
          '안전': '그림, 도표 등이 포함된 다양한 글을 읽고 주요 내용과 세부 내용을 파악해 봅니다. 글의 정보를 주의 깊게 살펴보고 핵심 단어를 찾아 읽으면 중요한 내용을 이해하거나 내용을 간추리는 데 도움이 됩니다.'
        },
        '통합 및 해석': {
          '주의': '글에 포함된 그림이나 도표를 참고하여 필요한 정보를 짐작해 봅니다. 글을 읽을 때 글쓴이나 등장인물의 마음을 헤아리면서 읽으면 글 읽기에 흥미가 생길 수 있습니다.',
          '경계': '일상 속 매체에서 명확하게 드러난 정보를 찾아봅니다. 자료 사이의 관계를 파악하며 글의 내용과 관련지어 읽는 습관을 기르면 글을 종합적으로 이해하는 데 도움이 됩니다.',
          '안전': '자료를 읽고 드러나지 않은 내용을 추론하거나 새로운 상황에 적용하여 문제를 해결해 봅니다. 글의 제목, 글의 짜임, 글쓴이의 의도를 고려하여 읽으면 글을 종합적으로 이해할 수 있습니다.'
        },
        '비판 및 평가': {
          '주의': '그림이 포함된 짧은 글을 읽고 자기 생각이나 느낌을 한두 문장으로 표현해 봅니다. 주어진 글과 그림의 내용을 꼼꼼하게 살펴보고 자기 생각이나 경험을 떠올리며 읽으면 내용을 이해하는 데 도움이 됩니다.',
          '경계': '친숙한 소재의 글을 읽고 글의 내용이 정확한지, 앞뒤가 잘 연결되는지 등을 살펴봅니다. 자료 속 글과 그림의 내용을 꼼꼼히 파악하면 비슷한 상황에서 글의 내용을 적용하는 데 도움이 됩니다.',
          '안전': '글을 읽고 내용을 분석하여 글의 내용과 표현이 적절한지 판단해 봅니다. 적절성을 판단하여 글을 읽으면 비판적 읽기 능력을 키울 수 있고 다른 상황에 적용하여 문제를 해결하는 데 도움이 됩니다.'
        }
      },
      'E_6': {
        '어휘': {
          '주의': '새로운 낱말이 나오면 앞뒤 문장을 읽어 보며 의미를 추측해 봅니다. 예상한 의미와 뜻이 비슷한 낱말을 넣어 내용이 자연스러운지 확인하는 연습을 꾸준히 하면 어휘력을 키우는 데 도움이 됩니다.',
          '경계': '학습 상황에서 새로운 낱말이 나오면 글의 앞뒤 문맥을 파악하여 낱말의 의미를 짐작하고 사전에서 뜻을 확인해 봅니다. 사전을 활용하여 글을 읽는 습관을 기르면 어휘력을 키우는 데 도움이 됩니다.',
          '안전': '맥락에 따라 다른 의미로 사용되는 낱말을 활용하여 짧은 문장을 만들어 보며 쓰임새를 익혀 봅니다. 비슷한 말, 반대되는 말 등 낱말의 관계를 생각하고 활용하면 어휘력을 키우는 데 도움이 됩니다.'
        },
        '탐색 및 확인': {
          '주의': '자료에서 자주 나오는 낱말이나 중요한 내용에 밑줄을 긋거나 동그라미 표시하며 읽어 봅니다. 글쓴이가 말하고자 하는 것이 무엇인지 생각하고 읽으면 글의 중심 내용을 파악하는 데 도움이 됩니다.',
          '경계': '자료에서 중요한 단어나 내용에 밑줄을 긋거나 동그라미 표시하며 읽어 봅니다. 글을 꼼꼼히 읽는 습관은 글의 내용을 정확히 파악하고 문제 해결의 단서를 찾는 데 도움이 됩니다.',
          '안전': '자료를 읽는 목적을 생각하며 시간 또는 사건의 순서, 원인과 결과에 따라 정리해 봅니다. 자료에 제시된 다양한 정보를 확인하며 읽으면 내용을 더 깊이 있게 이해하는 데 도움이 됩니다.'
        },
        '통합 및 해석': {
          '주의': '글을 읽기 전, 글의 제목이나 그림을 보고 내용을 예상합니다. 글을 읽을 때 글의 내용과 관련된 나의 경험을 떠올리며 장면을 상상하면 글에 드러나지 않은 내용을 추론하는 데 도움이 됩니다.',
          '경계': '글의 제목이나 중심 문장을 바탕으로 이어질 내용을 예상합니다. 예상하며 읽기, 배경지식을 활용하며 읽기와 같은 다양한 읽기 전략을 사용하면 드러나지 않은 내용을 추론하고 해석하는 데 도움이 됩니다.',
          '안전': '필요한 정보가 무엇인지 먼저 파악하고 글을 읽으면 쉽게 해석할 수 있습니다. 글과 함께 제시된 그림이나 도표의 정보를 서로 비교하여 통합적으로 읽으면 글에 드러나지 않은 내용을 추론하는 데 도움이 됩니다.'
        },
        '비판 및 평가': {
          '주의': '글을 읽고 글쓴이의 의견이 무엇인지 정리한 후 자기 생각을 떠올려 봅니다. 글쓴이의 의견과 내 생각의 공통점과 차이점을 비교하며 읽으면 비판적인 능력을 키우는 데 도움이 됩니다.',
          '경계': '글쓴이의 의견에 대해 궁금한 점을 질문하며 글을 읽어 봅니다. 글의 목적과 내용이 일치하는지, 주장의 근거가 타당한지 등을 생각하며 읽는 연습을 하면 글의 타당성을 평가하는 데 도움이 됩니다.',
          '안전': '글의 내용이 타당한지, 표현 방식이 적절한지 판단하며 읽습니다. 글을 읽을 때 경험이나 배경지식을 활용하면 문제 해결을 위한 적절한 대안을 찾을 수 있고 다른 상황에 적용하는 데 도움이 됩니다.'
        }
      },
      'M_2': {
        '어휘': {
          '주의': '이해하기 어려운 단어를 표시하며 읽고, 주변 사람들에게 질문하는 습관을 기릅니다. 친숙한 소재를 표현한 시각 자료를 단어 학습에 활용하면 어휘력을 키우는 데 도움이 됩니다. 친구와 함께 낱말 놀이를 해 봅니다.',
          '경계': '모르는 단어를 사전에서 찾거나 글의 앞뒤 내용으로 단어의 의미를 생각해 봅니다. 비슷한 의미로 사용되는 단어들을 찾아보고 활용하면 어휘력을 키우는 데 도움이 됩니다. 낯선 단어들로 나만의 단어장을 만들어 봅니다.',
          '안전': '어휘, 관용 표현 등을 일상생활과 학습 상황에서 맥락에 맞게 적용한 예문을 찾거나 만들어 봅니다. 과학, 사회, 예술, 환경 등 다양한 분야와 형식의 글을 많이 읽는 것이 도움이 됩니다.'
        },
        '탐색 및 확인': {
          '주의': '짧은 글을 처음부터 끝까지 천천히, 정확하게 소리 내어 읽습니다. 내용을 파악할 때 밑줄과 끊어 읽기 기호를 활용하여 읽으면 이해하는 데 도움이 됩니다. 이해한 내용을 말로 설명해 봅니다.',
          '경계': '문단 단위로 내용을 읽으며, 중요한 문장이나 단어에 표시하는 연습을 해 보면 글 전체를 정확하게 이해하는 데 도움이 됩니다. 글과 함께 주어진 시각 자료의 정보도 꼼꼼히 살펴봅니다.',
          '안전': '그림, 도표, 그래프 등이 포함된 복합자료를 서로 관련지으며 내용을 정확하게 이해하는 활동을 해 봅니다. 같은 주제에 대하여 서로 다른 관점이 드러나는 자료를 찾아 중심 내용과 세부 내용을 정리해 봅니다.'
        },
        '통합 및 해석': {
          '주의': '제목, 그림을 살피며 글을 읽고 글쓴이의 생각을 짐작해 봅니다. 글을 읽고 알게 된 점을 짧은 문장으로 써 봅니다. 주제가 같은 짧은 두 편의 글을 읽고 공통점과 차이점을 찾아 이야기해 봅니다.',
          '경계': '이야기 글을 읽으며 등장인물의 심리나 생각을 추측해 보는 활동과 줄거리를 써 보는 연습을 권합니다. 교과서에 나온 다양한 그림이나 도표의 정보를 글의 내용과 연관 지어 확인해 봅니다.',
          '안전': '읽기의 목적과 글의 구조를 고려하여 내용을 요약해 봅니다. 글에서 생략된 내용이나 글쓴이의 의도를 추론해 보고 자신의 근거를 제시해 봅니다. 맥락에 따른 담화 내용과 방식을 파악하여 함축된 의미를 유추합니다.'
        },
        '비판 및 평가': {
          '주의': '글의 내용과 관련된 나의 경험을 떠올리거나 주변 사람의 이야기를 들어봅니다. 이야기 글을 읽고, 자신의 느낌을 말해봅니다. 글을 읽는 자신을 칭찬해 줍니다. 꾸준히 글을 읽으면 읽기가 쉬워집니다.',
          '경계': '글을 읽고, 글쓴이의 의견과 자기 생각을 비교해 봅니다. 글과 관련한 자기 생각이나 비슷한 상황에 대해 다른 사람과 대화하면 생각의 폭을 넓힐 수 있습니다. 다양한 인터넷매체 속 정보가 믿을만한지 생각해 봅니다.',
          '안전': '일상에서 접하는 다양한 글에서 주제나 관점을 파악하고, 기준에 따라 내용의 타당성을 평가해 봅니다. 자신이 세운 기준이 타당한지 생각해 보고 다른 사람과 의견을 나누면 비판적 사고력을 기르는 데 도움이 됩니다.'
        }
      },
      'H_1': {
        '어휘': {
          '주의': '모르는 단어를 보면 표시한 후 인터넷 사전을 통해 단어의 뜻과 예문을 찾아봅시다. 일상에서 자주 쓰는 단어나 관용 표현의 정확한 의미를 파악하여 다른 사람에게 설명해 보면 어휘력을 키우는 데 도움이 됩니다.',
          '경계': '교과서에 모르는 단어가 나오면 그 뜻을 찾아 정리해 봅시다. 상황에 따라 다양한 의미로 쓰이는 단어는 사전을 찾아 그 뜻과 예문을 확인해 봅시다. 수준과 흥미를 고려한 짧은 글을 꾸준히 읽으며 단어를 익혀봅시다.',
          '안전': '글을 읽을 때 모르는 한자어나 관용 표현이 나오면 그 뜻을 사전에서 찾아 정리해 봅시다. 다양한 분야의 글을 읽으면서 새로운 단어를 접하고 그 의미를 찾아 일상과 학습 상황에서 사용하면 어휘력을 키울 수 있습니다.'
        },
        '탐색 및 확인': {
          '주의': '글을 이해하기 위해서 꼼꼼하게 읽는 습관을 기릅니다. 짧은 글을 처음부터 끝까지 천천히 소리 내어 읽으며 내용을 파악하고 반복해서 읽어 봅시다. 밑줄과 끊어 읽기 기호를 활용하면 내용을 이해하는 데 도움이 됩니다.',
          '경계': '각 문단에서 반복적으로 나타나는 단어나 어구를 찾으면서 읽으면 중심 내용을 파악하고 글 전체 내용을 이해하는 데 도움이 됩니다. 읽은 내용을 주변 사람에게 설명하면 내용을 정확하게 이해했는지 확인할 수 있습니다.',
          '안전': '도표, 그림 등 자료를 확인하며 글의 중심 내용 및 세부 내용을 파악합니다. 글의 특성 및 읽기 목적을 고려하여 중요 내용을 메모하며 읽어 봅시다. 관심 있는 분야의 다소 어렵다고 느껴지는 글을 읽는 것도 좋습니다.'
        },
        '통합 및 해석': {
          '주의': '글의 제목이나 목차를 주의 깊게 살펴보며 글의 내용을 짐작해 봅시다. 글을 읽을 때 관련된 자기 경험이나 배경지식 등을 떠올리며 읽으면 내용을 이해하는 데 도움이 됩니다. 읽은 글에 대해 친구들과 대화해 봅시다.',
          '경계': '글에 담긴 사실적인 정보뿐만 아니라 글쓴이가 전달하려는 의미도 파악해 봅시다. 제목의 의미 및 문장 간의 관계 파악하기, 질문하고 답하기 등 추론하는 연습을 통해 드러나지 않은 내용을 짐작하며 글을 이해해 봅시다.',
          '안전': '글 안의 그림, 도표, 그래프 등을 꼼꼼하게 살펴보면서 글을 정확하게 이해하고 생략된 내용을 추론해 봅시다. 글과 자료의 의미를 자신의 말로 바꾸어서 정리하면 통합적으로 해석하는 능력을 키울 수 있습니다.'
        },
        '비판 및 평가': {
          '주의': '글을 읽을 때 글 내용에 대한 자기 의견을 책의 여백에 적어 봅시다. 사실과 주장을 구별하며 읽는 연습을 하면 비판적 사고력을 기를 수 있습니다. 인터넷 정보를 볼 때도 사실인지 아닌지 판단하며 읽어 봅시다.',
          '경계': '글 속에 담긴 주장과 근거를 정리하며 읽어 봅시다. 글의 내용이나 글쓴이의 의도 등에 대해 질문하거나, 동의하는 부분과 동의하지 않는 부분을 생각하며 자기 의견을 제시하면, 비판적 사고력을 기를 수 있습니다.',
          '안전': '글의 내용이 타당한지, 표현이 적절한지 판단하며 읽어 봅시다. 글을 읽고 다른 독자들의 의견을 찾아 자기 생각과 비교하거나, 글에 드러난 상황과 비슷한 사례를 찾아 적용해 본다면 종합적 사고력을 기를 수 있습니다.'
        }
      }
    },
    subareaNamesOrder: ['어휘', '탐색 및 확인', '통합 및 해석', '비판 및 평가'],
  },
  numeracy: {
    cardTitle: '하위 영역별 기초 수리력 도달도*와 활용',
    explanationText: `수리력의 각 하위 영역에서 기초 수준에 도달했는지를 나타냅니다. <strong class="font-medium">1수준과 2수준을 구분하는 컷 점수</strong> 지점의 능력을 가진 학생이 해당 영역의 각 점수를 획득할 확률을 구하여, 아래로부터 누적 확률이 16%에 대응하는 원점수보다 낮으면 ‘주의’, 84%에 대응하는 원점수보다 높으면 ‘안전’, 그 사이의 원점수에 해당하면 ‘경계’입니다. 해석에 있어 ‘주의’는 해당 하위 영역에서 기초 수준에 도달하지 못한 단계, ‘경계’는 기초 수준의 경계에 있는 단계, ‘안전’은 기초 수준 이상에 있는 단계를 의미합니다. 체크된 부분은 학생의 해당 하위 영역별 도달 수준을 나타냅니다.`,
    icons: {
      '수와 양': numeracyIc01,
      '규칙과 관계': numeracyIc03,
      '모양과 측정': numeracyIc02,
      '자료와 불확실성': numeracyIc04,
    },
    adviceData: {
      'E_4': {
        '수와 양': {
          '주의': '수의 개념과 사칙계산의 기초를 다질 필요가 있으므로 수의 계열을 이해하기 위해 그림이나 교구 등 다양한 방법을 활용하고, 연산 수행 능력을 신장시키기 위해 기초 계산 학습을 반복해서 꾸준히 할 것을 권장합니다.',
          '경계': '소수와 분수의 개념을 정확히 이해하기 위해 그림이나 구체물을 활용하여 학습하는 것이 필요합니다. 자연수 및 분수와 소수의 계산 학습을 꾸준히 반복적으로 한다면 계산력이 향상될 것입니다.',
          '안전': '수의 개념에 대한 이해와 사칙계산 능력은 수리력의 기본입니다. 실생활에서 매일 다양하게 사용하는 수에 관심을 기울여 생활 속 문제를 해결하는 활동은 실제적인 수리력을 키우는 데 도움이 됩니다.'
        },
        '규칙과 관계': {
          '주의': '배열에서 규칙을 찾기 위해 필요한 정보를 정리하는 능력을 키워야 합니다. 변화의 과정을 표와 같이 한눈에 보기 쉽게 정리하는 방법 등을 활용하여 규칙을 찾아내는 연습을 꾸준히 하는 것이 도움이 됩니다.',
          '경계': '배열이나 식에서 규칙을 찾기 위해 기초적인 수학적 추론 능력을 키우는 것이 필요합니다. 주어진 조건에서 문제 해결에 필요한 정보를 파악하기 위해 변화 관계를 보기 쉽게 정리하는 연습이 도움이 됩니다.',
          '안전': '변화하는 배열이나 식에서 규칙을 찾아 수학적으로 표현하는 능력은 수리력에서 앞으로 더욱 중요하게 다뤄질 부분입니다. 다양한 변화 규칙이 있는 실생활의 문제를 해결해 보는 활동은 수리력 향상에 도움이 됩니다.'
        },
        '모양과 측정': {
          '주의': '여러 가지 도형의 정의부터 다질 필요가 있으므로 구체물로 된 도형을 활용하여 학습하면 도움이 됩니다. 길이, 무게, 들이의 개념을 익히기 위해 실생활에서 활용되는 상황을 직접 찾아보길 권장합니다.',
          '경계': '여러 가지 도형의 정의와 성질을 아는 것이 필요하므로 도형을 직접 조작하며 학습하는 것이 도움이 됩니다. 길이, 무게, 들이의 단위를 일상생활의 구체물로 떠올려 양감을 키우는 연습이 필요합니다.',
          '안전': '수리력은 도형의 개념을 이해하고 성질을 추론하는 과정에서 향상하므로 도형을 학습하며 타당하게 추론하는 연습이 필요합니다. 실생활에 적합한 측정 단위를 활용해 문제를 해결하는 활동은 응용력을 키우는 데 도움이 됩니다.'
        },
        '자료와 불확실성': {
          '주의': '주어진 자료를 기준에 맞게 정리하는 능력이 필요하므로 다양한 종류의 자료를 정리할 때 기준에 따라 다르게 표시하는 등 시각적인 방법을 활용하여 정확하게 정리해 보길 바랍니다.',
          '경계': '표로 제시된 자료를 그래프로 표현하는 연습이 필요합니다. 다양한 문제를 접하면서 여러 가지 그래프를 그려보길 바랍니다.',
          '안전': '수리력은 자료를 적합한 그래프로 나타내고 해석하는 과정에서 발달합니다. 실생활에서 사용하는 다양한 그래프에 제시된 정보를 해석하는 활동은 의사결정 능력을 키우는 데 도움이 됩니다.'
        }
      },
      'E_6': {
        '수와 양': {
          '주의': '수의 개념을 기르고 자연수의 혼합 계산 순서와 방법을 익히며 꾸준하게 반복 학습하기를 권장합니다. 이를 통해 수의 체계에 대한 기본적인 개념 이해도와 계산 능력이 향상될 것입니다.',
          '경계': '문제 상황에 알맞은 식을 세워보고 다양한 유형의 혼합 계산을 반복하기를 권장합니다. 자연수에서 간단한 분수, 소수로 확장하여 혼합 계산을 연습한다면 계산력이 점차 향상될 것입니다.',
          '안전': '수의 개념을 이해하고 정확하게 계산하는 능력은 수학 학습의 기초가 됩니다. 다양한 상황에서 분수, 소수 등의 혼합 계산을 활용하여 주어진 문제를 효과적으로 해결해 보는 활동은 수리력 향상에 도움이 됩니다.'
        },
        '규칙과 관계': {
          '주의': '수의 배열에서 규칙을 찾기 위해서 숫자 사이의 일정한 차이가 있는지 파악하는 연습을 권장합니다. 수의 배열에서 규칙을 찾는 연습을 꾸준히 한다면 수의 관계를 수학적으로 표현하는 능력이 향상될 것입니다.',
          '경계': '주어진 숫자 사이의 관계를 발견하고, 어떤 규칙이 있는지 설명하면서 찾아낸 규칙을 식으로 나타내볼 것을 권장합니다. 규칙을 찾아 정보를 빠르게 이해하고 다음 결괏값을 예측하는 능력이 향상될 것입니다.',
          '안전': '규칙성을 찾는 것은 수학 문제를 해결하는 데 필수적인 과정입니다. 문제에서 변화하는 상황과 관계를 파악하여 규칙과 관계를 식으로 나타내보는 활동은 문제를 해결하는 데 도움이 됩니다.'
        },
        '모양과 측정': {
          '주의': '구체물을 조작하거나 그림 자료 등 시각 자료를 활용한 활동으로 도형의 성질을 파악할 것을 권장합니다. 이를 통해 여러 가지 도형에 대한 기본적인 개념과 성질을 이해하는 능력이 향상될 것입니다.',
          '경계': '주변에서 도형을 찾아 특징을 생각해 보고 둘레와 넓이를 구하는 연습을 권장합니다. 이를 통해 여러 가지 도형의 개념과 성질을 이해하고 도형의 둘레와 넓이를 측정하는 능력이 향상될 것입니다.',
          '안전': '도형의 개념을 이해하고 양을 측정ㆍ어림하는 능력은 수학 능력 향상에 필수적입니다. 입체도형의 개념과 성질을 활용하여 입체도형을 구분하고, 겉넓이와 부피를 구하는 문제를 해결하는 활동은 수리력 향상에 도움이 됩니다.'
        },
        '자료와 불확실성': {
          '주의': '자료를 수집하고 정리하는 것에 관심을 가지고 자룟값이 그래프로 어떻게 표현되는지 살펴볼 것을 권장합니다. 이를 통해 그래프의 내용을 이해하고 자료를 그래프로 표현하는 능력이 향상될 것입니다.',
          '경계': '다양한 유형의 그래프가 사용되는 상황을 찾아보고 각 그래프의 특징을 파악해 볼 것을 권장합니다. 이를 통해 주어진 그래프에서 필요한 정보를 찾아 해석하는 능력이 향상될 것입니다.',
          '안전': '수학적 사고력은 그래프를 통해 주어진 정보를 바탕으로 예측, 추론하는 과정에서 발달합니다. 실생활 자료를 수집하고 정리하여 상황에 적합한 그래프로 만들고, 다양하게 해석하는 활동은 수리력 향상에 도움이 됩니다.'
        }
      },
      'M_2': {
        '수와 양': {
          '주의': '0보다 작은 수가 있음을 이해하는 것이 필요합니다. 간단한 계산이라 하더라도 항상 정답을 확인하고, 정답이 나올 때까지 꾸준히 연습한다면 기본적인 계산력을 기를 수 있습니다.',
          '경계': '정수의 혼합 계산에 대한 충분한 연습이 필요합니다. 간단한 분수, 소수가 포함된 유리수로 확장하여 혼합 계산의 순서에 따라 연습하면 연산 능력을 향상하는 데 도움이 됩니다.',
          '안전': '수에 대한 개념과 연산은 다양한 수학 학습의 기초가 됩니다. 자연수, 정수, 유리수 등으로 수의 확장을 이해하고, 일상생활 속 수학 문제를 사칙계산으로 해결하면 응용력을 기르는 데 도움이 됩니다.'
        },
        '규칙과 관계': {
          '주의': '수를 대신하여 문자를 사용할 수 있음을 이해하고, 문자가 사용된 식에 익숙해져야 합니다. 구체적인 수가 포함된 식에서 수를 문자로 바꾸는 연습은 문제를 해결하는 데 도움이 됩니다.',
          '경계': '문자를 사용한 식의 의미를 이해하고 간단한 식의 계산을 연습하는 활동이 필요합니다. 대입을 통해 식의 값을 구하고 간단한 방정식의 해를 찾아보는 연습은 문제를 해결하는 데 도움이 됩니다.',
          '안전': '방정식, 부등식, 함수는 일상생활 속 수학 문제를 해결하고 그 결과를 해석할 수 있는 매우 중요한 도구입니다. 여러 가지 상황을 다양한 식으로 나타내보는 활동은 문제를 해결하는 데 도움이 됩니다.'
        },
        '모양과 측정': {
          '주의': '도형의 기본 요소를 이해하고, 평면도형과 입체도형을 여러 방법으로 분류하여 공통점과 차이점을 찾아봅니다. 주변에 있는 사물을 어떤 도형으로 나타낼 수 있을지 생각해 보는 습관은 도형을 이해하는 데 도움이 됩니다.',
          '경계': '관찰이나 활동을 통해 점, 선, 면 등의 위치 관계를 찾아보고 다각형의 넓이, 변의 길이, 각의 크기 등을 구하는 방법과 입체도형의 부피, 겉넓이 등을 구하는 방법에 익숙해지길 바랍니다.',
          '안전': '다양한 방법을 통해 도형의 성질을 살펴보면서 공통으로 만족하는 성질 또는 그 도형만 가지고 있는 성질 등을 찾고, 올바른 기호를 사용해 그 관계를 표현해 보는 활동은 도형을 이해하는 데 도움이 됩니다.'
        },
        '자료와 불확실성': {
          '주의': '실생활에서 표와 그래프는 정보를 쉽게 전달하기 위해 사용됩니다. 뉴스나 신문 등의 매체에 나오는 표와 그래프를 많이 접해보고 익숙해지길 바랍니다. 표와 그래프에서 알 수 있는 정보가 무엇인지 생각해 보기를 권장합니다.',
          '경계': '주변에 있는 자료를 표와 그래프로 나타내기 위해 직접 그려보고 표와 그래프에서 다양한 정보를 찾아보길 바랍니다. 주어진 자료를 정리하고 해석하는 습관을 기르는 것을 권장합니다.',
          '안전': '목적에 따라 자료를 수집하고 정리하여 해석하는 연습은 현상을 분석하는 능력을 키워줍니다. 이는 통계자료를 근거로 자신의 의견을 전달하는 능력을 키우는 데 도움이 됩니다.'
        }
      },
      'H_1': {
        '수와 양': {
          '주의': '실수의 사칙계산 능력을 갖추는 것은 수리력 향상에 필수입니다. 꾸준한 연습을 통해 유리수와 무리수가 포함된 실수의 사칙계산에 익숙해지면 다른 영역의 능력을 높이는 데 도움이 됩니다.',
          '경계': '수와 집합의 연산에 대한 이해가 바탕이 될 때 추론 및 문제해결 능력이 향상됩니다. 반복적인 연습과 함께 연산의 결과가 왜 그렇게 나왔을지 생각해 보길 바랍니다.',
          '안전': '수와 연산과 관련된 여러 용어를 자기의 말로 표현할 수 있어야 합니다. 다양한 상황을 수학적으로 해석하려는 시도는 수리력 향상에 도움이 됩니다.'
        },
        '규칙과 관계': {
          '주의': '문자를 이용하여 나타낸 식에 익숙해져야 합니다. 변화하는 수의 관계를 간단한 함수나 식으로 표현해 보며 그 필요성을 깨닫는 활동이 필요합니다.',
          '경계': '인수분해는 방정식을 해결하는 데 필요한 방법임을 이해해야 합니다. 두 양 사이의 관계를 표현한 함수의 의미를 생각하면 문제를 해결하는 데 도움이 됩니다.',
          '안전': '여러 함수의 수학적 개념을 실생활과 연결해 보는 활동이 필요합니다. 공학 도구를 통해 조건을 다양하게 변화시키며 함수의 그래프의 특징을 살펴보는 것도 좋은 방법입니다.'
        },
        '모양과 측정': {
          '주의': '도형의 성질과 관계를 알기 위해서는 직접 종이에 그려보거나 공학 도구를 활용하는 활동은 다양한 도형에 익숙해지는 데 도움이 됩니다.',
          '경계': '문장이나 식으로 주어진 상황을 도형으로 표현하는 활동이 필요합니다. 공학 도구를 활용하여 도형의 방정식을 변화시키는 활동은 도형의 모양과 위치가 어떻게 변해가는지 탐구하는 데 도움이 됩니다.',
          '안전': '그림으로서의 도형과 방정식으로서의 도형을 함께 생각하여 문제를 종합적으로 탐색, 해결하는 활동이 필요합니다. 공학 도구를 활용하여 도형의 방정식을 만들어 보는 활동은 문제를 해결하는 데 도움이 됩니다.'
        },
        '자료와 불확실성': {
          '주의': '경우의 수를 셀 때는 빠짐없이 그리고 중복되지 않게 세야 하는 것을 염두에 두고 차근차근 접근해야 합니다. 표나 그래프를 볼 때는 가로축과 세로축이 무엇을 의미하는지 유념하며 살피는 습관이 필요합니다.',
          '경계': '간단한 자료일지라도 직접 정리하여 표나 그래프로 나타내고, 자료의 특징을 살펴봐야 합니다. 합의 법칙과 곱의 법칙을 이용하여 경우의 수, 확률을 구한 후 그 의미를 설명해 보는 활동을 권장합니다.',
          '안전': '자료를 수집하고 공학 도구로 분석하여 자료에서 얻은 관계를 효과적으로 시각화하는 연습이 필요합니다. 자료의 특징을 가장 잘 나타내는 대푯값이 무엇인지 생각해 보는 활동은 수리력 향상에 도움이 됩니다.'
        }
      }
    },
    subareaNamesOrder: ['수와 양', '규칙과 관계', '모양과 측정', '자료와 불확실성'],
  },
};

// 학년별 키 생성
const gradeKey = computed(() => {
  return `${props.schoolType}_${props.grade}`;
});

// 현재 콘텐츠
const currentContent = computed(() => allContent[props.type]);

// 동적 하위영역 점수 (API 데이터 기반)
const subareaScores = computed(() => {
  const names = currentContent.value.subareaNamesOrder;
  return names.map((name, index) => ({
    name,
    level: props.subAreaLevels[index] || ''
  }));
});

// 유효한 레벨이 있는지 확인
const hasValidLevels = computed(() => {
  return props.subAreaLevels.some(level => level && level !== '');
});

// 동적 조언 텍스트 생성
const getAdviceText = (areaName, level) => {
  const gradeData = currentContent.value.adviceData?.[gradeKey.value];
  const areaData = gradeData?.[areaName];
  const advice = areaData?.[level];
  
  if (advice) {
    // 첫 번째 영역 (어휘 또는 수와 양)인지 확인
    const firstAreaNames = ['어휘', '수와 양'];
    const isFirstArea = firstAreaNames.includes(areaName);
    
    const prefix = isFirstArea
      ? `기초 ${props.type === 'literacy' ? '문해력' : '수리력'} 기준에 비추어 보았을 때, <span class="point-color font-medium">${props.studentName}</span> 학생의 '${areaName}' 영역은 <span class="point-color font-medium">${level}</span> 수준입니다. `
      : `'${areaName}' 영역은 <span class="point-color font-medium">${level}</span> 수준입니다. `;
    
    return prefix + advice;
  }
  
  // 기본 메시지 (데이터가 없을 때)
  return `'${areaName}' 영역은 <span class="point-color font-medium">${level}</span> 수준입니다.`;
};
</script>

<template>
  <div class="card" :class="props.type">
    <h5 class="card-title" :class="props.type">{{ currentContent.cardTitle }}</h5>
    <n-flex :wrap="false" :size="20" class="pt-4">
      <div class="check-table">
        <table>
          <colgroup>
            <col style="width: 200px" />
            <col style="width: 75px" />
            <col style="width: 75px" />
            <col style="width: 75px" />
          </colgroup>
          <thead>
            <tr>
              <th></th>
              <th>주의</th>
              <th>경계</th>
              <th>안전</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="subareaName in currentContent.subareaNamesOrder" :key="subareaName">             
              <template v-if="subareaData = subareaScores.find(s => s.name === subareaName)">
                <td>
                  <n-flex :wrap="false" align="center" :size="0">
                    <div class="icon-wrap">
                      <img :src="currentContent.icons[subareaName]" :alt="`${subareaName}-아이콘`" />
                    </div>
                    <p>{{ subareaName }}</p>
                  </n-flex>
                </td>
                <td v-for="level in attainmentLevels" :key="level">
                  <p class="check-box" :class="{ 
                        'check-warning': subareaData.level === '주의' && subareaData.level === level,
                        'check-boundary': subareaData.level === '경계' && subareaData.level === level,
                        'check-safe': subareaData.level === '안전' && subareaData.level === level,
                  }"></p>
                </td>
              </template>
            </tr>
          </tbody>
        </table>
        <div class="text-wrap">
          <p>{{ props.type === 'literacy' ? '기초 문해력 도달도' : '기초 수리력 도달도' }}</p>
          <p v-html="currentContent.explanationText"></p>
        </div>
      </div>
      <ul v-if="hasValidLevels">
        <li v-for="subareaName in currentContent.subareaNamesOrder" :key="`advice-${subareaName}`">
          <template v-if="subareaData = subareaScores.find(s => s.name === subareaName && s.level && s.level !== '')">
            <p v-html="getAdviceText(subareaName, subareaData.level)"></p>
          </template>
        </li>
        <li>하위 영역에 대한 진단 정보를 바탕으로 주의 또는 경계 수준에 있는 영역을 보완하면 {{ props.type === 'literacy' ? '문해력' : '수리력' }}이 향상될 것으로 기대합니다.</li>
      </ul>
      <div v-else class="no-data-message">
        <p>{{ props.type === 'literacy' ? '문해력' : '수리력' }}을 미응시하였습니다.</p>
      </div>
    </n-flex>
  </div>
</template>

<style lang="scss" scoped>
.check-table {
    width: 560px;
    padding: 24px 0 0 20px;
    display: flex;
    flex-direction: column;
    align-items: center;
    table {
        width: 500px;
        font-size: 16px;
        font-weight: 500;
        text-align: center;   
        box-shadow: none;
        margin-bottom: 14px;
        * {
            border: 0;
        }       
        .check-box {
            height: 40px;           
            width: 80px;
            border-radius: 10px;
            display: flex;
            align-items: center;
            justify-content: center;
          &.check-warning::after,
          &.check-boundary::after,
          &.check-safe::after {
            content: '';
            width: 24px;
            height: 24px;
            background-image: url('@/assets/images/result/check.png'); 
          }
        }          
        th {height: 20px; font-weight: 500;}  
        td {                
            padding: 10px;
            height: 40px;
            width: 80px;
            &:nth-child(1) {
                img {
                    width: 24px;
                }
            }
            &:nth-child(2) .check-box {
                background-color: #FFC6C0;                
            }
            &:nth-child(3) .check-box {
                background-color: #FFED94;                
            }
            &:nth-child(4) .check-box {
                background-color: #C0F6B1;                
            }
            .n-flex {             
                border-radius: 5px;
                padding: 6px 8px 6px 16px;
                line-height: 1;
                p {
                  flex: 1;
                  text-align: center;
                }                
            }
        }      
    }
    .text-wrap {
      border-radius: 10px;
      padding: 8px 16px;
      p:nth-child(1) {font-weight: 500;}
      p:nth-child(2) {font-size: 16px; color: #333; letter-spacing: -.4px; text-align: justify;}
    }
}
.n-flex {
    ul {
      width: calc(100% - 560px);
      font-size: 16px;
      color: #333;
      padding: 12px 20px 0 24px;
      display: flex;
      flex-direction: column;
      gap: 8px;
      li {
        padding-left: 15px;
        position: relative;
        text-align: justify;
        word-break: keep-all;
        &::before {
          content: '';
          position: absolute;
          width: 3px;
          height: 3px;
          top: 9px;
          left: 0;
          border-radius: 100%;
          background-color: #333;          
        }
      }
    }
    
    .no-data-message {
      width: calc(100% - 560px);
      padding: 12px 20px 0 24px;
      font-size: 16px;
      color: #666;
      text-align: center;
    }        
}
.literacy {
  .check-table {
    table .n-flex {border: 1px solid #005BAC; color: #005BAC;}
    .text-wrap {
      background-color: rgba(0, 91, 172,.05);
      p:nth-child(1) {color: #005BAC;}
    }
  }
  &:deep(.n-flex ul li .point-color) {
    color: #005BAC;
  }
}
.numeracy {
  .check-table {
    table .n-flex {border: 1px solid #CE4E56; color: #CE4E56;}
    .text-wrap {
      background-color: rgba(248, 128, 135,.05);
      p:nth-child(1) {color: #CE4E56;}
    }
  }
  &:deep(.n-flex ul li .point-color) {
    color: #CE4E56;
  }
 
} 

</style>